'use client';

import { resetPassword, resetPasswordSendEmail } from '@/api/account';
import Button from '@/components/Button';
import Dialog from '@/components/Dialog';
import FormInput from '@/components/Form/FormInput';
import PasswordInput from '@/components/Form/PasswordInput';
import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { toast } from '@/components/Toast';
import logger from '@/utils/client-logger';
import { IS_DE } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { LeftArrowOutlined, QuestionOutlined } from '@zeelool/icon';
import { useCountDown } from 'ahooks';
import { useTranslations } from 'next-intl';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

interface ResetPasswordProps {
  show: boolean;
  email: string;
  onClose: () => void;
  onSuccess: (email: string) => void;
}

interface FormData {
  code: string;
  newPassword: string;
  confirmPassword: string;
}

// 默认表单值 - 移到组件外部确保稳定性
const DEFAULT_FORM_VALUES: FormData = {
  code: '',
  newPassword: '',
  confirmPassword: '',
};

// 移除独立的CountDownButton组件，改用actionBtnProps

const ResetPassword: React.FC<ResetPasswordProps> = ({ show, email, onClose, onSuccess }) => {
  const t = useTranslations('layout.resetPassword');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [showHelper, setShowHelper] = useState(false);
  const [webShow, setWebShow] = useState(show);

  const [targetDate, setTargetDate] = useState<number>(0); // 倒计时目标时间

  useEffect(() => {
    setWebShow(show);
  }, [show]);

  // 使用 ahooks 的 useCountDown Hook
  const [countdown] = useCountDown({
    targetDate, // 使用状态管理的目标时间
  });

  // 获取设备类型
  const { deviceType } = useContext(ResponsiveContext);
  const isMobile = useMemo(() => deviceType === EPlatform.WAP, [deviceType]);

  const methods = useForm<FormData>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onBlur',
  });

  const { control, handleSubmit, reset } = methods;

  // 启动倒计时的辅助函数
  const startCountDown = useCallback(() => {
    // 设置59秒后的时间作为目标时间
    const targetTime = Date.now() + 59 * 1000;
    setTargetDate(targetTime);
  }, []);

  // 停止倒计时的辅助函数
  const stopCountDown = useCallback(() => {
    setTargetDate(0);
  }, []);

  // 重新发送验证码
  const handleResend = useCallback(async () => {
    // 如果正在加载或还在倒计时中，则不允许重发
    if (resendLoading || countdown > 0) return;

    try {
      setResendLoading(true);
      const response = await resetPasswordSendEmail({ email });

      if (response.code === 200 || response.code === 411) {
        // 启动倒计时
        startCountDown();
      }
    } catch (error) {
      logger.log(error);
    } finally {
      // 无论组件是否挂载都要重置loading状态，避免状态残留
      setResendLoading(false);
    }
  }, [resendLoading, countdown, email, startCountDown]);

  // 提交表单
  const onSubmit = useCallback(
    async (data: FormData) => {
      if (loading) return;

      // 清空验证码错误信息
      methods.clearErrors('code');

      if (data.newPassword !== data.confirmPassword) {
        methods.setError('confirmPassword', {
          message: t('theTwoPasswordsDoNotMatch'),
        });
        return;
      }

      try {
        setLoading(true);
        const response = await resetPassword({
          email,
          code: data.code,
          password: data.newPassword,
          confirm_password: data.confirmPassword,
        });

        if (response.code === 200) {
          // 重置成功，显示成功提示
          toast.success(t('theAccountInformationHasBeenSaved'));
          // 关闭弹窗并调用成功回调
          onSuccess(email);
          handleClose();
        } else if (response.code === 411) {
          methods.setError('code', { message: t('pleaseEnterAValid4DigitCode') });
        }
      } catch (error) {
        logger.log(error);
      } finally {
        // 无论组件是否挂载都要重置loading状态，避免状态残留
        setLoading(false);
      }
    },
    [loading, email, onSuccess],
  );

  const handleClose = useCallback(() => {
    reset();
    setResendLoading(false); // 重置加载状态
    // 停止倒计时
    stopCountDown();
    onClose();
  }, [reset, onClose, stopCountDown]);

  const handleOpenHelper = () => {
    setWebShow(false);
    setShowHelper(true);
  };
  const handleCloseHelper = () => {
    setShowHelper(false);
    setWebShow(true);
  };

  // 表单验证规则
  const validationRules = useMemo(
    () => ({
      code: {
        required: t('verificationCodeIsRequired'),
        pattern: {
          value: /^[0-9]{4}$/,
          message: t('pleaseEnterAValid4DigitCode'),
        },
      },
    }),
    [],
  );

  // 当弹窗显示状态变化时处理
  useEffect(() => {
    if (show) {
      reset(DEFAULT_FORM_VALUES);
      setResendLoading(false); // 重置加载状态
      // 弹窗打开时立即启动倒计时
      startCountDown();
    } else {
      // 关闭弹窗时停止倒计时和重置状态
      stopCountDown();
      setResendLoading(false); // 确保关闭时重置加载状态
    }
  }, [show, reset, startCountDown, stopCountDown]);

  // 表单组件 - 改为普通函数，避免useCallback导致的重渲染问题
  const formContent = () => {
    return (
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className={`${isMobile ? 'mb-[12px]' : 'mb-[16px]'}`}>
            <FormInput
              name="code"
              control={control}
              label={isMobile ? '' : t('verificationCode')}
              required
              placeholder={t('verificationCode')}
              rules={validationRules.code}
              inputProps={{ maxLength: 4 }}
              clearable
              className="w-full"
              formItemClassName="mb-0"
              useNativeError
              sx={{
                '& .MuiInputBase-root': {
                  height: '46px',
                  borderRadius: isMobile ? '10px' : '6px',
                  backgroundColor: isMobile ? '#F4F4F4' : '#ffffff',
                  overflow: 'hidden',
                },
                '& .MuiInputLabel-root': {
                  fontSize: '14px',
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '14px',
                },
              }}
              actionBtnProps={{
                onClick: handleResend,
                loading: resendLoading,
                disabled: countdown > 0 || resendLoading,
                name: countdown > 0 ? `${Math.floor(countdown / 1000)}s` : t('resend'),
              }}
            />
          </div>

          <div className={`${isMobile ? 'mb-[12px]' : 'mb-[16px]'}`}>
            <PasswordInput
              name="newPassword"
              control={control}
              label={isMobile ? '' : t('newPassword')}
              required
              placeholder={t('newPassword')}
              clearable
              className="w-full"
              formItemClassName="mb-0"
              useNativeError
              onBlur={() => {
                if (methods.getValues('newPassword') !== methods.getValues('confirmPassword')) {
                  methods.setError('confirmPassword', {
                    message: t('theTwoPasswordsDoNotMatch'),
                  });
                } else {
                  methods.clearErrors('confirmPassword');
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '46px',
                  borderRadius: isMobile ? '10px' : '6px',
                  backgroundColor: isMobile ? '#F4F4F4' : '#ffffff',
                },
                '& .MuiInputLabel-root': {
                  fontSize: '14px',
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '14px',
                },
              }}
            />
          </div>

          <div className={`${isMobile ? 'mb-[32px]' : 'mb-[32px]'}`}>
            <PasswordInput
              name="confirmPassword"
              control={control}
              label={isMobile ? '' : t('confirmPassword')}
              required
              placeholder={t('confirmPassword')}
              clearable
              className="w-full"
              formItemClassName="mb-0"
              useNativeError
              onBlur={() => {
                if (methods.getValues('newPassword') !== methods.getValues('confirmPassword')) {
                  methods.setError('confirmPassword', {
                    message: t('theTwoPasswordsDoNotMatch'),
                  });
                } else {
                  methods.clearErrors('confirmPassword');
                }
              }}
              sx={{
                '& .MuiInputBase-root': {
                  height: '46px',
                  borderRadius: isMobile ? '10px' : '6px',
                  backgroundColor: isMobile ? '#F4F4F4' : '#ffffff',
                },
                '& .MuiInputLabel-root': {
                  fontSize: '14px',
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '14px',
                },
              }}
            />
          </div>

          {/* 提交按钮 */}
          <Button
            type="submit"
            variant="contained"
            loading={loading}
            disabled={loading}
            className="w-full !font-bb !text-[16px]"
            sx={{
              height: isMobile ? '50px' : '46px',
              borderRadius: isMobile ? '50px' : '4px',
            }}
          >
            {t('submit')}
          </Button>
        </form>
      </FormProvider>
    );
  };

  // 主内容部分 - 改为普通函数确保倒计时能正常更新
  const MainContent = () => {
    return (
      <>
        {/* 标题 - 仅PC端显示 */}
        {!isMobile && (
          <p className="text-center font-bb text-[20px] text-primary-black mt-0 mb-[12px]">
            {t('changePassword')}
          </p>
        )}

        {/* 副标题 */}
        <div
          className={`text-center text-[14px] font-br text-auxiliary-black leading-[20px] ${isMobile ? 'mb-[28px] mt-0 text-left' : 'mb-[33px] mt-[12px]'}`}
        >
          {t('verificationCodeToReset')}
          <br />
          {!IS_DE && t('verificationCodeSent')} <span className="text-primary-purple">{email}</span>
        </div>

        {/* 帮助信息 */}
        <div className={`flex justify-end items-center ${isMobile ? 'mb-[10px]' : 'mb-[10px]'}`}>
          <span
            className="flex items-center text-[12px] leading-[14px] text-secondary-text cursor-pointer"
            onClick={handleOpenHelper}
          >
            {t('dontReceiveEmail')}
            <QuestionOutlined className="text-[14px] ml-[3px]" />
          </span>
        </div>

        {/* 表单内容 */}
        {formContent()}
      </>
    );
  };

  // 帮助内容
  const HelpContent = () => (
    <>
      {/* 标题 - 仅在PC端显示，移动端Popup已有标题 */}
      {!isMobile && (
        <h4 className="text-[20px] font-bb text-primary-black pb-[20px] text-center">
          {t('dontReceiveEmailQuestion')}
        </h4>
      )}

      <div className={`mb-[32px] ${isMobile ? 'mt-[4px]' : ''}`}>
        <p className="text-[14px] leading-[22px] relative pl-[15px] mb-[12px] text-left text-primary-black before:content-[''] before:h-[6px] before:w-[6px] before:rounded-full before:bg-primary-black before:block before:absolute before:left-0 before:top-[8px]">
          {t('emailDelay')}
        </p>
        <p className="text-[14px] leading-[22px] relative pl-[15px] mb-[12px] text-left text-primary-black before:content-[''] before:h-[6px] before:w-[6px] before:rounded-full before:bg-primary-black before:block before:absolute before:left-0 before:top-[8px]">
          {t('checkJunkAndSpamFolders')}
        </p>
        <p className="text-[14px] leading-[22px] relative pl-[15px] mb-[12px] text-left text-primary-black before:content-[''] before:h-[6px] before:w-[6px] before:rounded-full before:bg-primary-black before:block before:absolute before:left-0 before:top-[8px]">
          {t('wait1MinuteAndTryAgain')}
        </p>
      </div>

      <Button
        variant="contained"
        className="w-full !font-bb !text-[16px]"
        sx={{
          height: isMobile ? '50px' : '46px',
          borderRadius: isMobile ? '50px' : '4px',
        }}
        onClick={handleCloseHelper}
      >
        {t('ok')}
      </Button>
    </>
  );

  return (
    <>
      {/* PC端弹窗 */}
      <WebOnly>
        <Dialog
          open={webShow}
          onClose={handleClose}
          footer={null}
          sx={{
            '& .MuiDialog-paper': {
              width: '400px',
              maxWidth: '100%',
              borderRadius: '8px',
            },
            '& .MuiDialogContent-root': {
              padding: '0',
            },
          }}
        >
          <div className="p-[40px] pt-[0]">{MainContent()}</div>
        </Dialog>
      </WebOnly>

      {/* 移动端全屏弹窗 */}
      <WapOnly>
        <Dialog
          open={show}
          onClose={handleClose}
          footer={null}
          fullScreen
          sx={{
            '& .MuiDialogTitle-root': {
              display: 'none',
            },
            '& .MuiDialog-paper': {
              width: '100%',
              maxWidth: '100%',
              borderRadius: '0',
              margin: '0',
              height: '100vh',
              maxHeight: '100vh',
            },
            '& .MuiDialogContent-root': {
              padding: '0',
              height: '100%',
              overflow: 'hidden',
              maxHeight: '100%',
            },
          }}
        >
          {/* 移动端标题栏 */}
          <div className="h-[44px] w-full text-center text-[#282828] font-bs text-[16px] bg-white flex items-center justify-center">
            <LeftArrowOutlined
              className="cursor-pointer text-[20px] absolute left-[20px]"
              onClick={handleClose}
            />
            {t('changePassword')}
          </div>

          {/* 移动端滚动内容 */}
          <div className="h-full overflow-y-auto pt-[20px] px-[24px] pb-[40px]">
            {MainContent()}
          </div>
        </Dialog>
      </WapOnly>

      {/* PC端帮助信息弹窗 */}
      <WebOnly>
        <Dialog
          open={showHelper}
          onClose={handleCloseHelper}
          footer={null}
          sx={{
            '& .MuiDialog-paper': {
              width: '400px',
              maxWidth: '100%',
            },
            '& .MuiDialogContent-root': {
              padding: '0',
            },
          }}
        >
          <div className="p-[24px] pt-[0]">
            <HelpContent />
          </div>
        </Dialog>
      </WebOnly>

      {/* 移动端帮助信息弹窗 */}
      <WapOnly>
        <Popup
          zIndex={1300}
          open={showHelper}
          onClose={handleCloseHelper}
          position="bottom"
          height="400px"
          title={t('dontReceiveEmailQuestion')}
          sx={{
            '& .MuiDrawer-paper': {
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              backgroundColor: '#ffffff',
            },
            '& .popup-content': {
              padding: '0',
            },
          }}
        >
          <div className="p-[16px_24px_30px] bg-white h-full box-border">
            <HelpContent />
          </div>
        </Popup>
      </WapOnly>
    </>
  );
};

export default ResetPassword;
